import { useCallback } from "react";
import { useMutation } from "@apollo/client";

import { getErrorMessage } from "@luce/core/apollo";

import {
  CancelSubscriptionDocument,
  type SubscriptionCancellationReasonEnum,
} from "../../__generated__/graphql";
import { useClientId } from "../../shared";

export type CancelSubscriptionArgs = {
  reason: SubscriptionCancellationReasonEnum;
  detail?: string;
};

export type CancelSubscriptionResult = { success: boolean; error?: string };

const CANCEL_ERROR = "Something went wrong. Please try again.";

export function useCancelSubscription(): {
  cancelSubscription: (args: CancelSubscriptionArgs) => Promise<CancelSubscriptionResult>;
  loading: boolean;
} {
  const clientId = useClientId();
  const [mutate, { loading }] = useMutation(CancelSubscriptionDocument);

  const cancelSubscription = useCallback(
    async ({ reason, detail }: CancelSubscriptionArgs): Promise<CancelSubscriptionResult> => {
      try {
        const { data } = await mutate({
          variables: {
            input: { clientId, cancellationReason: reason, cancellationDetail: detail },
          },
        });
        return { success: Boolean(data?.cancelSubscription?.subscription) };
      } catch (error) {
        return { success: false, error: getErrorMessage(error, CANCEL_ERROR) };
      }
    },
    [mutate, clientId],
  );

  return { cancelSubscription, loading };
}
